import {
	Vector3,
	StandardMaterial,
	MeshBuilder,
	Scene,
	Mesh,
	Vector4,
	CSG,
	AssetsManager,
	ArcRotateCamera,
	Engine,
	PointerDragBehavior,
	Ray,
	VertexBuffer,
	VertexData,
	Space,
	PointerEventTypes,
} from '@babylonjs/core';
import * as helpers from './helpers';
import { SeadevShedBuilder } from './ssb_builder';
import { NOTICES } from './ssb_constants';
import * as dimensions from './store/slices/dimensions';
export class ShedInteriorWall {
	private _shedBuilder: SeadevShedBuilder;
	public _scene: Scene;
	public _assetsManager: AssetsManager;
	public _polygonShedCutName = 'shapeCutInteriorWall';
	private _polygonShedCut: Mesh;
	public _canvas: HTMLCanvasElement;
	public _imagePath: String;
	public _camera: ArcRotateCamera;
	public _engine: Engine;
	public _defaultColor: string = '';
	public _material: any = {};
	public _shedType: string;
	public _shedTypeVersion: string;

	constructor(shedBuilder: SeadevShedBuilder) {
		this._shedBuilder = shedBuilder;
		this._scene = shedBuilder._scene;
		this._assetsManager = shedBuilder._assetsManager;
		this._canvas = shedBuilder._canvas;
		this._imagePath = this._canvas.getAttribute('data-image-path');
		this._camera = shedBuilder._camera;
		this._engine = shedBuilder._engine;
		this._shedType = shedBuilder._shedType;
		this._shedTypeVersion = shedBuilder._shedTypeVersion;
		this._material = shedBuilder.getSettingData('partial_wall_material');
		this._defaultColor = shedBuilder.getSettingData('partial_wall_color')
			? shedBuilder.getSettingData('partial_wall_color')
			: '';
	}

	/**
	 * Resolve wall width based on width type and wall rotation
	 *
	 * widthType:
	 * - user_input → use user-defined width
	 * - full_width → span full building width / depth (depends on rotation)
	 *
	 * rotation:
	 * - 'x' → side-to-side wall (use building width)
	 * - 'z' → gable-to-gable wall (use building depth)
	 */
	getWidthByType = (rotation: string, type: string, width: number) => {
		if (type === 'user_input') {
			return width;
		}
		if (rotation === 'x') {
			return Number(helpers.getBaseWidth());
		} else {
			return Number(helpers.getBaseDepth());
		}
	};

	/**
	 * Resolve wall height based on selected height type
	 *
	 * heightType:
	 * - user_input            → use user-defined height
	 * - fixed_height          → fixed absolute height
	 * - to_top_plate          → from floor to wall top plate
	 * - to_top_ceiling        → from floor to building ceiling
	 * - percent_wall_height   → percentage of wall height
	 */
	getHeightByType = (type: string, height: number) => {
		if (type === 'user_input' || type === 'fixed_height') {
			return height;
		}
		const wallHeight = dimensions.getWallHeight();
		const baseHeight = helpers.getBaseHeight();
		const getBuildingHeight = dimensions.getBuildingHeight();
		if (type === 'to_top_plate') {
			return wallHeight;
		} else if (type === 'to_top_ceiling') {
			return getBuildingHeight;
		} else if (type === 'percent_wall_height') {
			return helpers.toFixed3((baseHeight * height) / 100);
		}
		return 0;
	};

	/**
	 * Draw or update an interior partial wall
	 * - Resolve wall size from type (full / custom)
	 * - Reuse old position when editing
	 * - Create invisible box mesh as cutting target
	 * - Cut wall by interior building shapes
	 */
	drawWall = (data) => {
		const _this = this;
		let {
			wallName,
			name,
			heightType,
			inputWidth,
			widthType,
			inputHeight,
			rotation,
			isEdit,
			position,
		} = data;
		const oldMesh = _this._shedBuilder.getMeshByName(data.name);

		// Find position add mesh
		if (!position) {
			position = Vector3.Zero();
			if (isEdit && oldMesh) {
				const positionX =
					rotation === 'x' && widthType === 'full_width' ? 0 : oldMesh.position.x;
				const positionZ =
					rotation === 'z' && widthType === 'full_width' ? 0 : oldMesh.position.z;
				position = new Vector3(positionX, 0, positionZ);
			}
		} else {
			position = new Vector3(position._x, 0, position._z);
		}

		// Dispose old mesh
		if (oldMesh) oldMesh.dispose();

		// Data to draw mesh (height, width, depth)
		const height = this.getHeightByType(heightType, Number(inputHeight));
		const width = this.getWidthByType(rotation, widthType, Number(inputWidth));

		// Draw mesh use CreateBox
		const interiorWall = this.drawWallByExtrude(
			name,
			width,
			height,
			data.rotation,
			this._scene,
		);

		// set property
		interiorWall.bakeCurrentTransformIntoVertices();
		interiorWall.forceSharedVertices();
		interiorWall.convertToFlatShadedMesh();
		interiorWall.position = position.add(new Vector3(0, 0.02, 0));
		interiorWall.isPickable = false;
		interiorWall.isVisible = false;

		// const material = this.createMaterial(wallName, this._defaultColor, this._material);
		// interiorWall.material = material;
		// this.adjustUVMaterial(interiorWall, material, rotation, this._material);

		// add wall to data global
		this._shedBuilder._allPartialWalls[name] = {
			name: wallName,
			displayName: '',
			origin: {
				wallName,
				name,
				height,
				width,
				inputWidth,
				inputHeight,
				rotation: rotation,
				heightType,
				widthType,
			},
			visual: { wallName, name, height, width, rotation: rotation, heightType, widthType },
		};

		// cut mesh by building
		this.cutWallByShapeBuilding(
			interiorWall,
			this._polygonShedCut,
			this._shedBuilder._polygonFloor,
		);

		return interiorWall;
	};

	drawWallByExtrude = (name, width, height, rotation, scene) => {
		const thickness = 0.1;
		let shape = [
			new Vector3(-width / 2, 0, -height / 2),
			new Vector3(width / 2, 0, -height / 2),
			new Vector3(width / 2, 0, height / 2),
			new Vector3(-width / 2, 0, height / 2),
		];

		const wall = MeshBuilder.ExtrudePolygon(
			name,
			{
				shape,
				depth: thickness,
				sideOrientation: Mesh.DOUBLESIDE,
				updatable: false,
			},
			scene,
		);
		if (rotation === 'z') {
			wall.rotation.y = Math.PI / 2;
		}
		wall.rotation.x = -Math.PI / 2;
		wall.position.y = height / 2;

		return wall;
	};

	addWallToDOM = (key: string) => {
		const eventAddWallToDom = new CustomEvent('add-partial-wall', {
			detail: {
				key: key,
			},
		});
		window.dispatchEvent(eventAddWallToDom);
	};

	/**
	 * Draw an invisible extruded polygon used to cut interior space
	 * - Shape is defined in XZ plane from 2D input points
	 * - Extruded along Y, then rotated to align with interior cut direction
	 * - Mesh is disabled and used only for CSG / intersection logic
	 */
	drawPolygonSideToCutInterior = async (shapeCut: Vector3[], meshName: string) => {
		if (shapeCut.length === 0) return;
		const faceUV = [];
		const shape = shapeCut.map((i) => new Vector3(i.x, 0, i.y));

		// Find and remove old Mesh
		const exitsMesh = this._shedBuilder.getMeshByName(meshName);
		if (exitsMesh) {
			exitsMesh.dispose();
		}

		// Draw Mesh
		if (0 == faceUV.length) {
			faceUV[0] = new Vector4(0, 0, 1, 1);
			faceUV[2] = new Vector4(0, 1, 1, 0);
		}
		const polygon = MeshBuilder.ExtrudePolygon(
			meshName,
			{ shape, holes: [], depth: 60, faceUV, sideOrientation: Mesh.DOUBLESIDE },
			this._scene,
		);
		polygon.forceSharedVertices();
		polygon.convertToFlatShadedMesh();
		polygon.isPickable = false;
		polygon.rotation = new Vector3((3 * Math.PI) / 2, 0, 0);
		polygon.position = new Vector3(0, 0, -30);
		polygon.setEnabled(false);

		this._polygonShedCut = polygon;
	};

	/**
	 * Update visual data for a partial wall based on its mesh bounding box
	 * - Calculate wall width & height from world bounding box
	 * - Detect wall orientation from original rotation
	 * - Update visual metadata & display name for UI
	 */
	setVisualWallData = (key: string, mesh: Mesh) => {
		const boundingBox = mesh.getBoundingInfo().boundingBox;
		const wallData = this._shedBuilder._allPartialWalls[key];
		const offsetWallInner = 0.05 * 2;
		if (!wallData) return;
		let { rotation, height, width, heightType, widthType } = wallData['origin'];
		const baseHeight = Number(helpers.getBaseHeight());
		const visualHeight = Number(
			(boundingBox.maximumWorld.y - boundingBox.minimumWorld.y).toFixed(1),
		);

		if (heightType === 'to_top_plate' || height === baseHeight) {
			height = baseHeight;
		} else if (visualHeight > baseHeight) {
			height = visualHeight;
		}

		let visualWidth = 0;
		if (rotation === 'x') {
			visualWidth =
				Number((boundingBox.maximumWorld.x - boundingBox.minimumWorld.x).toFixed(1)) +
				offsetWallInner;
		} else {
			visualWidth =
				Number((boundingBox.maximumWorld.z - boundingBox.minimumWorld.z).toFixed(1)) +
				offsetWallInner;
		}

		width = Number(visualWidth) >= width ? width : Number(visualWidth.toFixed(1));

		const oldName = this._shedBuilder._allPartialWalls[key]['name'];
		const visual = { ...this._shedBuilder._allPartialWalls[key]['visual'] };
		const side = rotation === 'x' ? '(Side to Side)' : '(Gable to Gable)';

		this._shedBuilder._allPartialWalls[key]['visual'] = {
			...visual,
			width,
			height,
			rotation,
			mesh,
		};
		this._shedBuilder._allPartialWalls[key]['displayName'] =
			`${oldName} ${helpers.convertUnit(width).trim()} x ${helpers.convertUnit(height).trim()} ${side}`;
	};

	/**
	 * Recenter mesh geometry to its local XZ center
	 * - Move vertex data so geometry is centered at (0, 0) on XZ plane
	 * - Keep world position visually unchanged by offsetting mesh.position
	 *
	 * Useful when:
	 * - Pivot is off-center
	 * - You need consistent rotation / scaling around center
	 */
	recenterMeshXZ = (mesh: Mesh) => {
		// reset transform
		mesh.bakeCurrentTransformIntoVertices();
		mesh.rotationQuaternion = null;
		mesh.rotation.set(0, 0, 0);
		mesh.scaling.set(1, 1, 1);
		mesh.refreshBoundingInfo();
		const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
		const center = mesh.getBoundingInfo().boundingBox.center.clone();

		for (let i = 0; i < positions.length; i += 3) {
			positions[i] -= center.x; // X
			positions[i + 2] -= center.z; // Z
		}
		mesh.setVerticesData(VertexBuffer.PositionKind, positions);
		mesh.position.x += center.x;
		mesh.position.z += center.z;
		mesh.refreshBoundingInfo();
	};

	/**
	 * Cut interior wall mesh by building interior shapes (wall & floor)
	 * - Clone base wall mesh for display
	 * - Perform CSG subtraction with building volume
	 * - Recenter geometry to avoid pivot offset
	 * - Apply material, UV, drag behavior, and visual metadata
	 */
	cutWallByShapeBuilding = (wall: Mesh, shapeBuilding: Mesh, shapeFloor: Mesh) => {
		const wallCloneName = wall.name + '_display';
		const wallData = this._shedBuilder._allPartialWalls[wall.name];

		// Find and remove Partial Wall DisPlay
		const meshClone = this._shedBuilder.getMeshByName(wallCloneName);
		if (meshClone) {
			meshClone.dispose();
		}

		// Define CSG form Meshes
		let csgShapeShed = CSG.FromMesh(shapeBuilding);
		let csgShapeShedFloor = CSG.FromMesh(shapeFloor);
		let csgWall = CSG.FromMesh(wall);

		// Get intersect Wall
		const isIntersectBuilding = wall.intersectsMesh(shapeBuilding, false);
		const isIntersectFloor = wall.intersectsMesh(shapeFloor, false);

		// Subtract Csg wall with shape cut when have intersect
		let subCSG = csgWall;
		if (isIntersectBuilding) {
			subCSG = subCSG.subtract(csgShapeShed);
		}
		// structure_lean_to_with_front_porch
		// if (isIntersectFloor && (!isDrag || shedType === 'structure_lean_to_with_front_porch')) {
		// 	subCSG = subCSG.subtract(csgShapeShedFloor);
		// }
		if (isIntersectFloor) {
			subCSG = subCSG.subtract(csgShapeShedFloor);
		}

		// Convert cst to mesh
		let newMesh = subCSG.toMesh(wallCloneName, wall.material, this._scene, true);
		// newMesh.parent = wall.parent;

		newMesh.isPickable = true;

		const material = this.createMaterial(wallCloneName, this._defaultColor, this._material);
		newMesh.material = material;
		this.adjustUVMaterial(newMesh, material, wallData.origin.rotation, this._material);

		this.recenterMeshXZ(newMesh);

		shapeBuilding.setEnabled(false);
		wall.setEnabled(false);

		// Update visual data wall
		this.setVisualWallData(wall.name, newMesh);

		// Add drag event
		this.addDragForInterior(newMesh, wall, wallData.origin.rotation);
		// Add element wall to DOM
		this.addWallToDOM(wall.name);
	};

	/**
	 * Create or reuse a StandardMaterial by name
	 * - Reuse existing material if already created
	 * - Create new material from provided material data
	 * - Apply color and specular settings
	 */
	createMaterial = (
		materialName: string,
		colorCode: string,
		materialData: {
			diffuse?: string;
			bump?: string;
			specular?: string;
			opa?: number;
			specular_power?: number;
		},
	) => {
		let material = this._scene.getMaterialByName(materialName) as StandardMaterial;
		if (material === null) {
			const color = { type: 'color', code: colorCode };
			const { diffuse, bump, specular, opa, specular_power } = materialData;
			material = this._shedBuilder.createMaterial(
				materialName,
				diffuse,
				bump,
				opa,
				specular,
				color,
			);
			material.specularPower = specular_power;
		}

		return material;
	};

	adjustUVMaterial = (
		mesh: Mesh,
		material: StandardMaterial,
		rotation: string,
		materialData: {
			diffuse?: string;
			bump?: string;
			specular?: string;
			opa?: number;
			specular_power?: number;
		},
	) => {
		const boundingBox = mesh.getBoundingInfo().boundingBox;
		let height = Number((boundingBox.maximumWorld.y - boundingBox.minimumWorld.y).toFixed(1));
		let width = 0;
		if (rotation === 'x') {
			width = Number((boundingBox.maximumWorld.x - boundingBox.minimumWorld.x).toFixed(1));
		} else {
			width = Number((boundingBox.maximumWorld.z - boundingBox.minimumWorld.z).toFixed(1));
		}

		const vScale = helpers.toFixed3(height / (8 / materialData['vScale']));
		const uScale = helpers.toFixed3(width / (8 / materialData['uScale']));

		if (uScale) {
			if (material.bumpTexture) material.bumpTexture['uScale'] = uScale;
			if (material.diffuseTexture) material.diffuseTexture['uScale'] = uScale;
			if (material.specularTexture) material.specularTexture['uScale'] = uScale;
			if (material.opacityTexture) material.opacityTexture['uScale'] = uScale;
		}

		if (vScale) {
			if (material.bumpTexture) material.bumpTexture['vScale'] = vScale;
			if (material.diffuseTexture) material.diffuseTexture['vScale'] = vScale;
			if (material.specularTexture) material.specularTexture['vScale'] = vScale;
			if (material.opacityTexture) material.opacityTexture['vScale'] = vScale;
		}
	};

	addDragForInterior = (mesh: Mesh, parentMesh: Mesh, rotation: string) => {
		const dragBehavior = new PointerDragBehavior({ dragPlaneNormal: new Vector3(0, 1, 0) });
		dragBehavior.useObjectOrientationForDragging = false;
		mesh.addBehavior(dragBehavior);
		dragBehavior.moveAttached = false;
		dragBehavior.updateDragPlane = false;
		const groundMesh = this._shedBuilder._polygonFloor;
		dragBehavior.onDragStartObservable.add((e) => {
			const cameraDirection = this._camera.getForwardRay().direction;
			let newNormal: Vector3;
			if (Math.abs(cameraDirection.y) < 0.5) {
				newNormal = new Vector3(0, 1, 0);
			} else {
				newNormal = new Vector3(cameraDirection.x, 0, cameraDirection.z).normalize();
			}
			(dragBehavior as any).dragPlaneNormal = newNormal;
		});

		dragBehavior.onDragEndObservable.add((event) => {
			this.cutWallByShapeBuilding(parentMesh, this._polygonShedCut, groundMesh);
			this.addEditButton(mesh, parentMesh);
			const removeAddWallToDom = new CustomEvent('update-price-outside-ts');
			window.dispatchEvent(removeAddWallToDom);
		});

		dragBehavior.onDragObservable.add((eventData) => {
			const delta = eventData.delta;
			const speed = 1;
			const tryX = mesh.position.x + delta.x * speed;
			const tryZ = mesh.position.z + delta.z * speed;
			let finalX = mesh.position.x;
			let finalZ = mesh.position.z;

			if (this.canMoveOnGround(mesh, groundMesh, tryX, tryZ, rotation)) {
				finalX = tryX;
				finalZ = tryZ;
			} else {
				if (this.canMoveOnGround(mesh, groundMesh, tryX, mesh.position.z, rotation)) {
					finalX = tryX;
				}

				if (this.canMoveOnGround(mesh, groundMesh, mesh.position.x, tryZ, rotation)) {
					finalZ = tryZ;
				}
			}

			mesh.position.x = finalX;
			mesh.position.z = finalZ;
			mesh.position.y = parentMesh.position.y;

			parentMesh.position = new Vector3(finalX, parentMesh.position.y, finalZ);
		});
	};

	holeWall = (intersectMeshes, wallData) => {
		const _this = this;
		wallData.forEach((wall) => {
			const wallName = wall.name;
			const wallNameClone = `${wallName}_clone`;
			const meshClone = this._shedBuilder.getMeshByName(wallNameClone);
			if (meshClone) {
				meshClone.dispose();
			}
			const oldWall = this._shedBuilder.getMeshByName(wallName);
			if (null === oldWall) {
				return;
			}
			if (intersectMeshes.length <= 0) {
				return oldWall.setEnabled(true);
			}
			const mainMeshCSG = CSG.FromMesh(oldWall);
			for (let i = 0; i < intersectMeshes.length; i++) {
				mainMeshCSG.subtractInPlace(CSG.FromMesh(intersectMeshes[i]));
			}
			const wallCut = mainMeshCSG.toMesh(
				wallNameClone,
				oldWall.material,
				_this._scene,
				false,
			);
			wallCut.isPickable = false;
			wallCut.setParent(oldWall.parent);
			wallCut.visibility = oldWall.visibility;
			oldWall.setEnabled(false);
		});
	};

	/**
	 * Check whether a mesh can be placed at (x, z) on the ground
	 * - Cast downward rays from 4 bottom corners of the mesh
	 * - Ensure all corners hit the ground mesh
	 * - Used for drag validation / movement constraints
	 */
	canMoveOnGround = (mesh, groundMesh, x, z, rotation) => {
		const bounds = mesh.getBoundingInfo().boundingBox.extendSizeWorld;
		const wallThickness = 0.1;
		const adjustZ = rotation === 'x' ? -wallThickness / 2 : 0.05;
		const adjustX = rotation === 'z' ? -wallThickness / 2 : 0.05;
		const points = [
			new Vector3(x - bounds.x + adjustX, 20, z - bounds.z + adjustZ),
			new Vector3(x + bounds.x - adjustX, 20, z - bounds.z + adjustZ),
			new Vector3(x - bounds.x + adjustX, 20, z + bounds.z - adjustZ),
			new Vector3(x + bounds.x - adjustX, 20, z + bounds.z - adjustZ),
		];
		return points.every((p) => {
			const ray = new Ray(p, Vector3.Down());
			const hit = this._scene.pickWithRay(ray, (m) => m === groundMesh);
			return hit?.hit;
		});
	};

	/**
	 * Validate and rebuild all partial interior walls
	 * - Recalculate walls that depend on building dimensions
	 * - Re-cut walls when floor or interior shape changes
	 * - Remove walls that no longer fit inside the building
	 */
	validateAllPartialWall = () => {
		const shapeBuildingFloor = this._shedBuilder._polygonFloor;
		const shapeBuilding = this._polygonShedCut;
		const allWalls = { ...this._shedBuilder._allPartialWalls };
		for (let wall in allWalls) {
			const originData = this._shedBuilder._allPartialWalls[wall]['origin'];
			let mesh = this._shedBuilder.getMeshByName(wall);
			if (!mesh) continue;
			if (
				originData.widthType === 'full_width' ||
				(originData.heightType !== 'fixed_height' && originData.heightType !== 'user_input')
			) {
				const dataDraw = { ...originData, isEdit: true };
				mesh = this.drawWall(dataDraw);
			}

			const isIntersectFloor = mesh.intersectsMesh(shapeBuildingFloor, false);
			if (isIntersectFloor) {
				this.cutWallByShapeBuilding(mesh, shapeBuilding, shapeBuildingFloor);
			} else {
				this.removeMesh(wall);
				this._shedBuilder.createAlert(NOTICES.itemNotFit);
			}
		}
	};

	removeEditButton = () => {
		const button = jQuery('#js-component-button-group');
		if (button) {
			button.prop('disabled', true);
			button.remove();
		}
	};

	addEditButton = (mesh: Mesh, parentMesh: Mesh) => {
		const meshShape = this._shedBuilder.getMeshByName('sphere');
		if (meshShape) meshShape.dispose();
		const anchorRemove = MeshBuilder.CreateSphere('sphere', { diameter: 0.001 }, this._scene);
		anchorRemove.isPickable = false;

		this.removeEditButton();
		let x = mesh.position.x;
		let y = 0;
		let z = mesh.position.z;
		const parentMeshName = parentMesh.name;
		anchorRemove.position = new Vector3(x, y, z);
		const navHeight = jQuery('#wrapper-navbar nav.navbar').outerHeight();

		const buttonGroup = document.createElement('div');
		buttonGroup.className = 'component-button-group';
		buttonGroup.id = 'js-component-button-group';

		anchorRemove.onBeforeRenderObservable.add(async () => {
			const coordinates = await Vector3.Project(
				Vector3.Zero(),
				anchorRemove.getWorldMatrix(),
				this._scene.getTransformMatrix(),
				this._camera.viewport.toGlobal(
					this._engine.getRenderWidth(true),
					this._engine.getRenderHeight(true),
				),
			);

			buttonGroup.style.top = `${coordinates.y - 16 + navHeight}px`;
			buttonGroup.style.left = `${coordinates.x - 16}px`;
		});

		const removeButton = document.createElement('BUTTON');
		removeButton.className = 'component-remove-button component-button-item';
		removeButton.id = 'js-component-remove-button';
		removeButton.style.background = `url("${this._imagePath}close-button.svg")`;

		const editButton = document.createElement('BUTTON');
		editButton.className = 'component-edit-button component-button-item';
		editButton.id = 'js-component-edit-button';
		editButton.style.background = `url("${this._imagePath}edit-component.svg")`;

		buttonGroup.appendChild(editButton);
		buttonGroup.appendChild(removeButton);

		editButton.onclick = (e) => {
			e.preventDefault();
			jQuery(
				`.added-components.interior-wall [data-wall-name="${parentMeshName}"] .edit-wall`,
			).trigger('click');
		};

		removeButton.onclick = (e) => {
			e.preventDefault();
			this.removeMesh(parentMeshName);
		};

		document.body.appendChild(buttonGroup);
	};

	removeMesh = (name: string) => {
		const mesh = this._shedBuilder.getMeshByName(name);
		const meshDisplay = this._shedBuilder.getMeshByName(name + '_display');
		if (mesh) mesh.dispose();
		if (meshDisplay) meshDisplay.dispose();
		delete this._shedBuilder._allPartialWalls[name];
		jQuery(`.added-item[data-wall-name="${name}"]`).remove();
		const removeAddWallToDom = new CustomEvent('update-price-outside-ts');
		window.dispatchEvent(removeAddWallToDom);
	};

	/**
	 * Enable or disable picking for all displayed partial walls
	 * - Used when switching between edit / view modes
	 * - Only affects visible (display) wall meshes
	 */
	setPickerWall = (status: boolean) => {
		for (let wall in this._shedBuilder._allPartialWalls) {
			const wallCloneName = wall + '_display';
			const meshClone = this._shedBuilder.getMeshByName(wallCloneName);
			if (meshClone) {
				meshClone.isPickable = status;
			}
		}
	};
}
